<template>
  <div class="calc-trigger">
    <el-tooltip content="Калькулятор конвертации" placement="bottom">
      <el-button
        icon="el-icon-money"
        circle
        size="mini"
        type="primary"
        @click="open"
      ></el-button>
    </el-tooltip>
    <el-dialog :visible.sync="visible" width="440px" append-to-body>
      <template slot="title">
        <div class="calc-title">
          <span>Калькулятор конвертации</span>
          <el-tooltip content="Сбросить к исходному состоянию" placement="top">
            <el-button
              icon="el-icon-refresh-left"
              circle
              size="mini"
              class="calc-title__reset"
              @click="reset"
            ></el-button>
          </el-tooltip>
        </div>
      </template>
      <div v-loading="loading">
        <div class="pair-quickpicks" v-if="quickPairs.length">
          <el-button
            v-for="p in quickPairs"
            :key="p.giveKey + '-' + p.getKey"
            size="small"
            round
            :type="isActivePair(p) ? 'primary' : 'default'"
            @click="selectPair(p)"
            >{{ p.giveKey }}/{{ p.getKey }}</el-button
          >
        </div>
        <div v-else-if="loaded" class="calc-hint">
          Пресеты ещё не настроены — обратитесь к суперадмину
        </div>

        <div class="subcategory-pills" v-if="availablePresets.length">
          <el-button
            v-for="p in availablePresets"
            :key="p._id"
            size="small"
            round
            :type="selectedPresetId === p._id ? 'primary' : 'default'"
            @click="selectPreset(p._id)"
            >{{ p.name }}</el-button
          >
        </div>

        <template v-if="giveKey && getKey">
          <div class="pair-hint">
            Нам отдают {{ giveKey }}, мы отдаём {{ getKey }}
          </div>

          <div class="amount-row">
            <span class="amount-row__icon">{{ giveKey }}</span>
            <el-input v-model="giveAmount" placeholder="Сумма" @input="onGiveInput"></el-input>
            <el-button
              icon="el-icon-document-copy"
              circle
              size="mini"
              @click="copy(giveAmount)"
            ></el-button>
          </div>
          <div class="amount-row">
            <span class="amount-row__icon">{{ getKey }}</span>
            <el-input v-model="getAmount" placeholder="Сумма" @input="onGetInput"></el-input>
            <el-button
              icon="el-icon-document-copy"
              circle
              size="mini"
              @click="copy(getAmount)"
            ></el-button>
          </div>

          <div class="calc-info" v-if="marketRate">
            <div>
              Курс: 1 {{ giveKey }} = {{ numToString(marketRate) }} {{ getKey }}
            </div>
            <div v-if="lastResult && lastResult.range">
              <div v-if="lastResult.range.type === 'percent'">
                Комиссия: {{ numToString(lastResult.range.value) }}%
                <template v-if="lastResult.commissionAmount">
                  ({{ numToString(lastResult.commissionAmount) }} {{ giveKey }})
                </template>
              </div>
              <div v-else>
                Комиссия: {{ numToString(lastResult.range.value) }} ₽ (фикс)
                <template v-if="lastResult.commissionAmount">
                  ({{ numToString(lastResult.commissionAmount) }} {{ giveKey }})
                </template>
              </div>
              <div class="calc-hint">
                Диапазон: {{ lastResult.range.from }}–{{
                  lastResult.range.to === null ? "∞" : lastResult.range.to
                }}
                ₽
              </div>
            </div>
            <div v-if="lastResult && lastResult.extraGiveFee">
              Безусловный тариф: {{ numToString(lastResult.extraGiveFee) }} {{ giveKey }}
            </div>
            <div v-if="lastResult && selectedPreset && lastResult.range === null" class="calc-hint calc-hint--warning">
              Введённая сумма не попадает ни в один диапазон пресета «{{ selectedPreset.name }}»
            </div>
            <div v-if="lastResult && lastResult.belowMinimum" class="calc-hint calc-hint--warning">
              Сумма за вычетом комиссии меньше безусловного тарифа ({{ numToString(lastResult.extraGiveFee) }} {{ giveKey }}) — результат обмена равен 0
            </div>
          </div>
        </template>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import { getCommissionPresets } from "@/api";
import { calculatePresetConversion } from "@/utils/commissionCalculator";
import { dedupeValutesByKey } from "@/utils/valutes";
import { subscribeToLiveValutes, refreshLiveValutes, getLiveValutesState } from "@/utils/liveValutes";
import Decimal from "decimal.js";

export default {
  name: "CommissionCalculatorModal",
  data: () => ({
    visible: false,
    loading: false,
    loaded: false,
    presets: [],
    giveKey: null,
    getKey: null,
    giveAmount: "",
    getAmount: "",
    selectedPresetId: null,
    lastEdited: null,
    lastResult: null,
    pollTimer: null,
    pollIntervalMs: 30 * 1000,
    unsubscribeValutes: null
  }),
  computed: {
    // Курсы теперь общие на всё приложение (см. @/utils/liveValutes) —
    // раньше этот компонент сам гонял getValutes() каждые 30 сек своим
    // отдельным таймером параллельно с HeaderRates.vue, который делал
    // ровно то же самое: два несинхронизированных запроса вместо одного.
    // См. dedupeValutesByKey в @/utils/valutes — тот же дедуп-по-key с
    // TRC20 как канонической записью для USDT, общий для всех мест, где
    // нужна ровно одна запись Valutes на ключ (иначе :key на v-for
    // задублируется, и Vue путает, какая опция чему соответствует при
    // перерисовке — см. баг: giveKey "RUB" превращался в "CASHRUB").
    dedupedValutes() {
      return dedupeValutesByKey(getLiveValutesState().valutes);
    },
    giveValute() {
      return this.dedupedValutes.find(v => v.key === this.giveKey);
    },
    getValute() {
      return this.dedupedValutes.find(v => v.key === this.getKey);
    },
    // Через Decimal, не сырое JS-деление — эта же цифра идёт и в строку
    // "Курс: ...", и как giveRateToRub/getRateToRub-основа для
    // calculatePresetConversion, так что должна быть так же точна.
    marketRate() {
      if (!this.giveValute || !this.getValute) return null;
      if (!this.giveValute.course || !this.getValute.course) return null;
      return new Decimal(this.giveValute.course).div(this.getValute.course).toNumber();
    },
    // Пары, у которых реально есть хотя бы один пресет — единственный
    // способ выбрать направление в калькуляторе (свободный выбор валют
    // убрали: расчёт возможен только по заранее забитым пресетам).
    quickPairs() {
      const seen = new Set();
      const result = [];
      for (const p of this.presets) {
        const key = `${p.giveKey}-${p.getKey}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({ giveKey: p.giveKey, getKey: p.getKey });
      }
      return result;
    },
    availablePresets() {
      if (!this.giveKey || !this.getKey) return [];
      return this.presets.filter(
        p => p.giveKey === this.giveKey && p.getKey === this.getKey
      );
    },
    selectedPreset() {
      return (
        this.availablePresets.find(p => p._id === this.selectedPresetId) ||
        null
      );
    },
  },
  watch: {
    // el-dialog закрывается и своим крестиком/кликом вне окна, напрямую
    // синкая visible через :visible.sync — в обход нашего open(), поэтому
    // останавливать поллинг нужно именно здесь, а не только в одном методе.
    visible(isVisible) {
      if (isVisible) return;
      this.stopPolling();
      if (this.unsubscribeValutes) {
        this.unsubscribeValutes();
        this.unsubscribeValutes = null;
      }
    },
    giveAmount() {
      if (this.lastEdited !== "give") return;
      this.recalc("give");
    },
    getAmount() {
      if (this.lastEdited !== "get") return;
      this.recalc("get");
    },
    // Больше не сбрасывают selectedPresetId здесь: giveKey/getKey теперь
    // меняются только через selectPair (свободных селектов нет), а он сам
    // сразу выставляет корректный пресет для новой пары — если бы watcher
    // тоже сбрасывал его в null, это стёрло бы выбор selectPair (Vue
    // применяет реактивные присваивания до срабатывания watch, так что
    // порядок был бы "выставили пресет → сбросили в null").
    giveKey() {
      this.recalcFromLastEdited();
    },
    getKey() {
      this.recalcFromLastEdited();
    },
    selectedPresetId() {
      this.recalcFromLastEdited();
    }
  },
  methods: {
    isActivePair(p) {
      return p.giveKey === this.giveKey && p.getKey === this.getKey;
    },
    // Калькулятор теперь живёт в GlobalHeaderWidgets и переживает переход
    // между страницами (см. коммит), поэтому единственный способ вернуться
    // к чистому состоянию — явная кнопка, а не просто закрыть/открыть
    // модалку. valutes/presets/loaded не трогаем — это не пользовательский
    // ввод, а загруженные данные.
    reset() {
      this.giveKey = null;
      this.getKey = null;
      this.giveAmount = "";
      this.getAmount = "";
      this.selectedPresetId = null;
      this.lastEdited = null;
      this.lastResult = null;
    },
    // Раз калькулятор считает только по пресетам, выбор пары без активного
    // пресета бессмысленен — сразу подставляем первую подкатегорию, дальше
    // пользователь может переключить её кнопками ниже.
    selectPair(p) {
      this.giveKey = p.giveKey;
      this.getKey = p.getKey;
      const firstPreset = this.presets.find(
        x => x.giveKey === p.giveKey && x.getKey === p.getKey
      );
      this.selectedPresetId = firstPreset ? firstPreset._id : null;
    },
    selectPreset(id) {
      this.selectedPresetId = id;
    },
    copy(text) {
      if (!text) return;
      this.$copyText(String(text)).then(
        () => this.$message({ type: "success", message: "Скопировано" }),
        () => this.$message({ type: "error", message: "Не удалось скопировать" })
      );
    },
    onGiveInput() {
      this.lastEdited = "give";
    },
    onGetInput() {
      this.lastEdited = "get";
    },
    recalcFromLastEdited() {
      if (this.lastEdited === "give" || this.lastEdited === "get") {
        this.recalc(this.lastEdited);
      }
    },
    // Расчёт возможен только по выбранному пресету — без него (пара ещё не
    // выбрана, или её единственный пресет только что пропал и полёт ещё не
    // подобрал замену) просто нечего считать.
    recalc(editedField) {
      if (!this.marketRate || !this.selectedPreset) {
        this.lastResult = null;
        return;
      }
      const amount = parseFloat(
        editedField === "give" ? this.giveAmount : this.getAmount
      );
      if (!Number.isFinite(amount) || amount <= 0) {
        this.lastResult = null;
        return;
      }
      const result = calculatePresetConversion({
        preset: this.selectedPreset,
        giveRateToRub: this.giveValute.course,
        getRateToRub: this.getValute.course,
        amount,
        editedField
      });
      this.lastResult = result;
      if (!result || result.result === null || result.result === undefined) {
        // Диапазон не найден (сумма вне всех диапазонов пресета) — не
        // оставляем на экране цифру от предыдущего, не относящегося к делу
        // расчёта: без этого противоположное поле молча показывало старый
        // результат как будто он посчитан для только что введённой суммы.
        if (editedField === "give") {
          this.getAmount = "";
        } else {
          this.giveAmount = "";
        }
        return;
      }
      if (editedField === "give") {
        this.getAmount = String(result.result);
      } else {
        this.giveAmount = String(result.result);
      }
    },
    async open() {
      this.visible = true;
      // Подписка держит общий поллинг курсов живым, пока модалка открыта
      // (и не заводит второй таймер, если он уже крутится ради HeaderRates
      // в шапке — см. @/utils/liveValutes). Плюс форс-рефреш прямо сейчас,
      // не дожидаясь ближайшего тика общего поллинга — как и раньше, при
      // открытии хотим самые свежие курсы немедленно.
      this.unsubscribeValutes = subscribeToLiveValutes();
      await refreshLiveValutes(true);
      await this.refreshPresetsAndRecalc();
      // Пресеты — это только наши данные, HeaderRates про них не знает,
      // так что поллим их сами, как и раньше. Курсы больше отдельно не
      // поллим — уже поллятся через подписку выше.
      this.startPolling();
    },
    // Общая логика для открытия модалки и для фонового поллинга: подтянуть
    // свежие пресеты, и если выбранный пропал (удалили/отключили) —
    // подставить первый оставшийся для этой же пары, а не просто обнулить:
    // калькулятор теперь всегда считает через пресет, без него полностью бы
    // замолчал. Пересчитываем уже введённую сумму по свежим данным — иначе
    // экран молча показывает результат, посчитанный по устаревшим
    // курсу/пресету.
    async refreshPresetsAndRecalc(silent) {
      await this.loadPresets(silent);
      if (
        this.selectedPresetId &&
        !this.availablePresets.some(p => p._id === this.selectedPresetId)
      ) {
        this.selectedPresetId = this.availablePresets.length
          ? this.availablePresets[0]._id
          : null;
      }
      this.recalcFromLastEdited();
    },
    // setTimeout, который сам себя перепланирует после завершения запроса
    // (а не setInterval) — так же, как в DashboardValuteExchangeRateToRub,
    // чтобы медленный запрос не породил параллельный следующий тик.
    startPolling() {
      this.stopPolling();
      this.pollTimer = setTimeout(async () => {
        await this.refreshPresetsAndRecalc(true);
        if (this.visible) this.startPolling();
      }, this.pollIntervalMs);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    // silent=true — для фонового поллинга: не гоняем v-loading на весь
    // модал каждые 30 сек (перекрывало бы модалку прямо во время ввода
    // суммы пользователем), показываем спиннер только на первую загрузку.
    async loadPresets(silent) {
      if (!silent) this.loading = true;
      try {
        const { data } = await getCommissionPresets();
        if (data.status) this.presets = data.result;
        this.loaded = true;
      } catch (error) {
        if (!silent) this.handleResponseError(error);
      } finally {
        if (!silent) this.loading = false;
      }
    }
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.unsubscribeValutes) this.unsubscribeValutes();
  }
};
</script>
<style scoped>
.calc-trigger {
  display: inline-block;
}
.calc-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* место под родной крестик el-dialog справа (~28px), не наезжаем на него */
  padding-right: 28px;
  font-size: 18px;
}
.pair-quickpicks {
  margin-bottom: 10px;
}
.pair-quickpicks .el-button {
  margin-left: 0;
  margin-right: 6px;
  margin-bottom: 6px;
}
.pair-hint {
  color: #606266;
  font-size: 0.85rem;
  margin: 2px 0 12px;
}
.amount-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.amount-row__icon {
  min-width: 44px;
  text-align: center;
  font-size: 0.75rem;
  color: #909399;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 5px 4px;
}
.subcategory-pills {
  margin: 4px 0 14px;
}
.subcategory-pills .el-button {
  margin-left: 0;
  margin-right: 6px;
  margin-bottom: 6px;
}
.calc-info {
  font-size: 0.85rem;
  color: #606266;
}
.calc-hint {
  color: #909399;
}
.calc-hint--warning {
  color: #e6a23c;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/CommissionCalculatorModal.vue